import type { AlarmDeliveryResponse, AlarmProbeActionState, AlarmProbeRunRecord } from "@starsach/shared-types";
import { useAlarmDelivery } from "../../api/hooks";
import { Hint } from "../../components/Hint";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, RelativeTime, StatusPill } from "../../components/ui";
import { useFormat } from "../../lib/useFormat";

type Standing = { status: string; tone: "ok" | "warning" | "critical" | "neutral" };

/** Red when the newest run of either kind failed, amber when the daily check has stopped, green otherwise. */
export function standing(d: AlarmDeliveryResponse, now = Date.now()): Standing {
  if (!d.latest) return { status: "not run yet", tone: "neutral" };
  if (d.latest.status === "failed" || d.lastRoundTrip?.status === "failed") return { status: "failing", tone: "critical" };
  if (now - Date.parse(d.latest.checked_at) > d.staleAfterHours * 3_600_000) return { status: "stale", tone: "warning" };
  return { status: "delivering", tone: "ok" };
}

const ACTION_TONE: Record<AlarmProbeActionState, "ok" | "critical" | "warning"> = { succeeded: "ok", failed: "critical", missing: "warning" };

/**
 * Platform > Alarm delivery (#538). The runtime alarm probe's newest
 * results: whether every alarm topic has a confirmed subscriber, and
 * whether the last deploy's round trip on the probe alarm reached its
 * topic. A failing probe is red here and raises its own alarm.
 */
export function AlarmDeliveryCard() {
  const q = useAlarmDelivery();
  const fmt = useFormat();
  const title = <>Alarm delivery<Hint id="platform.alarmDelivery" label="what the probe checks" /></>;
  if (q.isPending) return <Card title={title} className="mb-4"><LoadingState label="Loading alarm delivery" /></Card>;
  if (q.isError) return <Card title={title} className="mb-4"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></Card>;
  const d = q.data;
  const s = standing(d);
  const failures = [...new Set([...(d.latest?.failures ?? []), ...(d.lastRoundTrip?.failures ?? [])])];
  const trip = d.lastRoundTrip?.round_trip;

  return (
    <Card title={title} className="mb-4">
      <p className="mb-3 flex flex-wrap items-center gap-2 text-sm">
        <StatusPill status={s.status} tone={s.tone} />
        {s.status === "not run yet" && <span className="text-neutral-600 dark:text-neutral-400">The probe checks the alarm topics daily and makes a round trip after every Central deploy.</span>}
        {s.status === "stale" && <span className="text-neutral-600 dark:text-neutral-400">No check in the last {fmt.count(d.staleAfterHours, "hour", "hours")}: the probe itself may have stopped.</span>}
      </p>

      {failures.length > 0 && (
        <div role="alert" className="mb-3 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100">
          <p className="font-medium">Alarms may not be reaching anyone</p>
          <ul className="mt-1 list-disc pl-5">
            {failures.map((f) => <li key={f}>{f}</li>)}
          </ul>
        </div>
      )}

      {d.latest && (
        <dl className="mb-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-neutral-500">Topics checked</dt>
          <dd><RelativeTime iso={d.latest.checked_at} /> <span className="text-xs text-neutral-500">{triggerText(d.latest)}</span></dd>
          <dt className="text-neutral-500">Round trip</dt>
          <dd>
            {d.lastRoundTrip && trip ? (
              <span className="flex flex-wrap items-center gap-2">
                <RelativeTime iso={d.lastRoundTrip.checked_at} />
                <StatusPill status={trip.ok ? "passed" : "failed"} tone={trip.ok ? "ok" : "critical"} />
                <span className="text-xs text-neutral-500">
                  ALARM action <StatusPill status={trip.alarm_action} tone={ACTION_TONE[trip.alarm_action]} />, OK action <StatusPill status={trip.ok_action} tone={ACTION_TONE[trip.ok_action]} />, {fmt.count(trip.seconds, "second", "seconds")}
                </span>
              </span>
            ) : (
              <span className="text-neutral-500">None yet: the first comes after the next Central deploy</span>
            )}
          </dd>
        </dl>
      )}

      {d.latest && (
        <DataTable
          caption="Alarm topics and their confirmed subscribers"
          rows={d.latest.topics}
          rowKey={(t) => t.topic_arn}
          filter={false}
          sortBy={(t) => t.name}
          empty={<EmptyState title="No alarm topics were checked" />}
          columns={[
            { key: "name", header: "Topic", render: (t) => <span className="break-all" title={t.topic_arn}>{t.name}</span> },
            { key: "confirmed", header: "Confirmed", numeric: true, render: (t) => fmt.number(t.confirmed) },
            { key: "pending", header: "Pending", numeric: true, render: (t) => fmt.number(t.pending) },
            { key: "protocols", header: "Delivered by", render: (t) => t.protocols.map(protocolName).join(", ") || "Nobody" },
            { key: "ok", header: "Status", render: (t) => <StatusPill status={t.ok ? "listening" : "nobody listening"} tone={t.ok ? "ok" : "critical"} /> },
          ]}
        />
      )}
    </Card>
  );
}

function triggerText(run: AlarmProbeRunRecord): string {
  return run.trigger === "schedule" ? "daily check" : run.trigger === "deploy" ? "after a deploy" : "run by hand";
}

/** Chatbot subscribes over HTTPS; saying so is the point of the column. */
function protocolName(p: string): string {
  return p === "https" ? "Slack (AWS Chatbot)" : p === "email" ? "Email" : p;
}
